import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Search, Radio, User, LogOut, LayoutDashboard, Shield, Heart, Menu, X, Ticket, MapPin, PartyPopper, Flag, BedDouble, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { playChime } from "@/lib/chime";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/explorer", label: "Explorer" },
  { to: "/evenements", label: "Événements" },
  { to: "/live", label: "LIVE", live: true },
  { to: "/etablissements", label: "Établissements" },
  { to: "/residences", label: "Résidences" },
  { to: "/faq", label: "FAQ" },
];

const CAT_CHIPS = [["bar", "Bars"], ["maquis", "Maquis"], ["lounge", "Lounges"], ["club", "Clubs"], ["restaurant", "Restos"]];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sug, setSug] = useState({ establishments: [], events: [] });
  const [showSug, setShowSug] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSug({ establishments: [], events: [] }); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q: term } })
        .then((r) => setSug({ establishments: (r.data.establishments || []).slice(0, 5), events: (r.data.events || []).slice(0, 5) }))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goTo = (path) => { setShowSug(false); setQ(""); setOpen(false); nav(path); };
  const hasSug = sug.establishments.length + sug.events.length > 0;

  const submit = (e) => {
    e.preventDefault();
    setShowSug(false);
    nav(`/recherche?q=${encodeURIComponent(q)}`);
  };
  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  const [isHost, setIsHost] = useState(false);
  const [pendingReq, setPendingReq] = useState(0);
  const prevPending = useRef(null);
  useEffect(() => {
    prevPending.current = null;
    if (!user) { setIsHost(false); setPendingReq(0); return; }
    const load = () => api.get("/residences/host/me").then((r) => {
      const n = r.data.pending_requests || 0;
      if (r.data.host && prevPending.current !== null && n > prevPending.current) {
        const added = n - prevPending.current;
        playChime();
        toast(added > 1 ? `📩 ${added} nouvelles demandes de réservation` : "📩 Nouvelle demande de réservation", {
          description: "Acceptez ou refusez depuis la cloche ou l'onglet Demandes.", duration: 10000,
          action: { label: "Voir", onClick: () => nav("/mon-espace-hote?tab=demandes") },
        });
      }
      prevPending.current = n;
      setIsHost(!!r.data.host); setPendingReq(n);
    }).catch(() => setIsHost(false));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user, nav]);
  const badge = pendingReq > 0 && <span data-testid="host-pending-badge" title={`${pendingReq} demande(s) en attente`} className="ml-1.5 min-w-5 h-5 px-1.5 rounded-full bg-white text-[#FF5A4F] text-xs font-black grid place-items-center">{pendingReq > 99 ? "99+" : pendingReq}</span>;
  const firstName = user ? (user.name || user.email || "").split(/[\s@]/)[0] : "";

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <img src="/media/babimix-logo-light-sm.png" alt="BABIMIX" width="48" height="48" className="h-12 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className="px-3 py-2 text-sm font-semibold whitespace-nowrap text-slate-300 hover:text-[#FF5A00] transition-colors flex items-center gap-1.5">
                {l.live && <span className="w-2 h-2 rounded-full bg-[#FF5A00] live-dot" />}
                {l.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submit} ref={boxRef} className="hidden md:flex items-center flex-1 max-w-xs relative">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="navbar-search-input" value={q}
                onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
                onFocus={() => setShowSug(true)}
                placeholder="Rechercher un bar, maquis, événement..."
                className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[#FF5A00]/60" />
            </div>
            {showSug && q.trim().length >= 2 && (
              <div data-testid="search-suggestions" className="absolute top-full left-0 right-0 mt-2 card-surface rounded-2xl border border-white/10 shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
                <div className="flex flex-wrap gap-1.5 p-3 border-b border-white/5">
                  {CAT_CHIPS.map(([v, l]) => (
                    <button type="button" key={v} data-testid={`sug-cat-${v}`} onClick={() => goTo(`/etablissements?category=${v}`)} className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 border border-white/10 hover:border-[#FF5A00]/60 text-slate-200">{l}</button>
                  ))}
                </div>
                {!hasSug ? (
                  <div className="p-4 text-sm text-slate-400">Aucun résultat pour « {q} »</div>
                ) : (
                  <>
                    {sug.establishments.map((e) => (
                      <button type="button" key={e.id} data-testid={`sug-est-${e.slug}`} onClick={() => goTo(`/etablissement/${e.slug}`)}
                        className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-2 border-b border-white/5">
                        <MapPin className="w-4 h-4 text-[#FF5A00] shrink-0" />
                        <span className="flex-1 min-w-0"><span className="font-semibold text-sm block truncate">{e.name}</span><span className="text-xs text-slate-400 capitalize">{e.category} · {e.commune}</span></span>
                      </button>
                    ))}
                    {sug.events.map((e) => (
                      <button type="button" key={e.id} data-testid={`sug-ev-${e.id}`} onClick={() => goTo(`/evenement/${e.slug}`)}
                        className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-2 border-b border-white/5">
                        <PartyPopper className="w-4 h-4 text-[#FF1493] shrink-0" />
                        <span className="flex-1 min-w-0"><span className="font-semibold text-sm block truncate">{e.name}</span><span className="text-xs text-slate-400">{e.date} · {e.commune}</span></span>
                      </button>
                    ))}
                    <button type="button" onClick={() => goTo(`/recherche?q=${encodeURIComponent(q)}`)} className="w-full text-center px-4 py-2.5 text-sm font-semibold text-[#FF5A00] hover:bg-white/5">Voir tous les résultats →</button>
                  </>
                )}
              </div>
            )}
          </form>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            {user === false && (
              <>
                <Button data-testid="business-cta" variant="ghost" className="text-sm font-semibold text-slate-200 hover:text-[#FF5A00]" onClick={() => nav("/business")}>BABIMIX Business</Button>
                <Button data-testid="login-btn" onClick={() => nav("/connexion")} className="flame-gradient text-white font-bold rounded-full">Connexion</Button>
              </>
            )}
            {user && (
              <div className="flex items-center gap-1">
                <NotificationBell />
                <Link to="/favoris" data-testid="favorites-link" className="p-2 text-slate-300 hover:text-[#FF5A00]"><Heart className="w-5 h-5" /></Link>
                <Link to="/mes-billets" data-testid="tickets-link" className="p-2 text-slate-300 hover:text-[#FFD600]"><Ticket className="w-5 h-5" /></Link>
                <Link to="/mes-revendications" data-testid="claims-link" className="p-2 text-slate-300 hover:text-[#FF5A00]" title="Mes revendications"><Flag className="w-5 h-5" /></Link>
                <Link to="/mes-reservations" data-testid="my-bookings-link" className="p-2 text-slate-300 hover:text-[#FF5A00]" title="Mes réservations"><BedDouble className="w-5 h-5" /></Link>
                {isHost && <Button data-testid="host-space-link" size="sm" onClick={() => nav(pendingReq > 0 ? "/mon-espace-hote?tab=demandes" : "/mon-espace-hote")} className="bg-[#FF5A4F] hover:bg-[#e84b40] text-white rounded-full font-bold"><Home className="w-4 h-4 mr-1" />Espace propriétaire{badge}</Button>}
                {isAdmin && <Button data-testid="admin-link" variant="ghost" size="sm" onClick={() => nav("/admin")}><Shield className="w-4 h-4 mr-1" />Admin</Button>}
                <Button data-testid="dashboard-link" variant="ghost" size="sm" onClick={() => nav("/business")}><LayoutDashboard className="w-4 h-4 mr-1" />Business</Button>
                <button data-testid="logout-btn" onClick={logout} className="p-2 text-slate-300 hover:text-[#FF5A00]" title="Déconnexion"><LogOut className="w-5 h-5" /></button>
                <div data-testid="navbar-user-name" className="flex items-center gap-2 pl-1" title={user.name}>
                  <div className="w-8 h-8 rounded-full flame-gradient grid place-items-center text-sm font-bold">{(user.name || "U")[0].toUpperCase()}</div>
                  <span className="text-sm font-semibold text-slate-100 max-w-[120px] truncate hidden xl:inline">{firstName}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            {user === false && (
              <Button data-testid="mobile-login-btn" onClick={() => nav("/connexion")} className="flame-gradient text-white font-bold rounded-full px-4 h-9 text-sm">Connexion</Button>
            )}
            {user && (
              <span data-testid="mobile-user-name" className="text-sm font-semibold text-slate-100 max-w-[90px] truncate">{firstName}</span>
            )}
            {user && <span className="md:hidden"><NotificationBell /></span>}
            {isHost && pendingReq > 0 && (
              <Link to="/mon-espace-hote?tab=demandes" data-testid="mobile-host-pending-chip" title={`${pendingReq} demande(s) en attente`} className="relative p-1.5 text-[#FF5A4F]"><Home className="w-5 h-5" /><span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[#FF5A4F] text-white text-[10px] font-black grid place-items-center">{pendingReq}</span></Link>
            )}
            {user && (
              <button data-testid="mobile-logout-btn" onClick={logout} className="flex items-center gap-1 px-3 h-9 rounded-full border border-white/20 text-sm font-semibold text-slate-200 hover:text-[#FF5A00]"><LogOut className="w-4 h-4" />Déconnexion</button>
            )}
          </div>
          <button data-testid="mobile-menu-toggle" aria-label="Menu" className="p-2 lg:hidden shrink-0" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu-panel" className="lg:hidden glass border-t border-white/10 px-4 py-4 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <form onSubmit={submit} className="relative mb-3 md:hidden">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="mobile-search-input" value={q} onChange={(e) => { setQ(e.target.value); setShowSug(true); }} placeholder="Rechercher un bar, maquis, événement..."
              className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm outline-none" />
            {showSug && q.trim().length >= 2 && (
              <div data-testid="mobile-search-suggestions" className="mt-2 card-surface rounded-2xl border border-white/10 overflow-hidden max-h-[50vh] overflow-y-auto">
                <div className="flex flex-wrap gap-1.5 p-3 border-b border-white/5">
                  {CAT_CHIPS.map(([v, l]) => (
                    <button type="button" key={v} data-testid={`msug-cat-${v}`} onClick={() => goTo(`/etablissements?category=${v}`)} className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-slate-200">{l}</button>
                  ))}
                </div>
                {!hasSug ? (
                  <div className="p-3 text-sm text-slate-400">Aucun résultat pour « {q} »</div>
                ) : (
                  <>
                    {sug.establishments.map((e) => (
                      <button type="button" key={e.id} onClick={() => goTo(`/etablissement/${e.slug}`)} className="w-full text-left px-3 py-2.5 hover:bg-white/5 flex items-center gap-2 border-b border-white/5">
                        <MapPin className="w-4 h-4 text-[#FF5A00] shrink-0" />
                        <span className="flex-1 min-w-0"><span className="font-semibold text-sm block truncate">{e.name}</span><span className="text-xs text-slate-400 capitalize">{e.category} · {e.commune}</span></span>
                      </button>
                    ))}
                    {sug.events.map((e) => (
                      <button type="button" key={e.id} onClick={() => goTo(`/evenement/${e.slug}`)} className="w-full text-left px-3 py-2.5 hover:bg-white/5 flex items-center gap-2 border-b border-white/5">
                        <PartyPopper className="w-4 h-4 text-[#FF1493] shrink-0" />
                        <span className="flex-1 min-w-0"><span className="font-semibold text-sm block truncate">{e.name}</span><span className="text-xs text-slate-400">{e.date} · {e.commune}</span></span>
                      </button>
                    ))}
                    <button type="button" onClick={() => goTo(`/recherche?q=${encodeURIComponent(q)}`)} className="w-full text-center px-3 py-2.5 text-sm font-semibold text-[#FF5A00] hover:bg-white/5">Voir tous les résultats →</button>
                  </>
                )}
              </div>
            )}
          </form>
          {user && <p data-testid="mobile-greeting" className="text-sm text-slate-300">Bonjour, <b className="text-slate-100">{user.name || firstName}</b></p>}
          {isHost && <Link to="/mon-espace-hote" onClick={() => setOpen(false)} data-testid="mobile-host-space-btn" className="flex items-center justify-center gap-2 py-3 rounded-full bg-[#FF5A4F] text-white font-bold"><Home className="w-4 h-4" />Mon espace propriétaire{pendingReq > 0 && <span data-testid="mobile-host-pending-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-white text-[#FF5A4F] text-xs font-black grid place-items-center">{pendingReq}</span>}</Link>}
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 font-semibold text-slate-200">{l.label}</Link>
          ))}
          <Link to="/tarifs" onClick={() => setOpen(false)} data-testid="mobile-pricing-link" className="block py-2 font-semibold text-slate-200">Abonnements</Link>
          {user && <div className="border-t border-white/10 pt-2 mt-2" />}
          {user && <Link to="/favoris" onClick={() => setOpen(false)} data-testid="mobile-favorites-link" className="block py-2 font-semibold text-slate-200">Mes favoris</Link>}
          {user && <Link to="/mes-billets" onClick={() => setOpen(false)} data-testid="mobile-tickets-link" className="block py-2 font-semibold text-slate-200">Mes billets</Link>}
          {user && <Link to="/mes-revendications" onClick={() => setOpen(false)} data-testid="mobile-claims-link" className="block py-2 font-semibold text-slate-200">Mes revendications</Link>}
          {user && <Link to="/mes-reservations" onClick={() => setOpen(false)} data-testid="mobile-my-bookings-link" className="block py-2 font-semibold text-slate-200">Mes réservations</Link>}
          {!isHost && <Link to="/mon-espace-hote" onClick={() => setOpen(false)} data-testid="mobile-host-link" className="block py-2 font-semibold text-[#FF5A4F]">Publier mon logement</Link>}
          <div className="pt-2 flex gap-2">
            {user === false ? (
              <>
                <Button data-testid="mobile-business-cta" variant="outline" onClick={() => { setOpen(false); nav("/business"); }} className="flex-1 rounded-full font-bold">BABIMIX Business</Button>
                <Button data-testid="mobile-menu-login-btn" onClick={() => { setOpen(false); nav("/connexion"); }} className="flame-gradient flex-1 rounded-full font-bold">Connexion</Button>
              </>
            ) : (
              <>
                <Button onClick={() => { setOpen(false); nav("/business"); }} className="flame-gradient flex-1 rounded-full font-bold">Business</Button>
                {isAdmin && <Button variant="outline" onClick={() => { setOpen(false); nav("/admin"); }} className="rounded-full">Admin</Button>}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
